// Stores
import { useReactiveVar } from "@apollo/client";
import { chart } from "../../stores";

// Utils
import { utils } from "../../helpers";

// WP React stuff
import { useState, useEffect } from '@wordpress/element';
const classNames = require("classnames");

/**
 * ChartLegend Component
 *
 * Renders a customizable legend for chart data, supporting both array-based
 * and object-based structures. Colors are either derived from data or fallback
 * to reactive chart configuration.
 *
 * @param {Object} props - Component properties
 * @param {Array|Object} props.data - Data to generate the legend from
 * @param {boolean} [props.percentage] - If true, values will be shown as percentages
 * @param {number} [props.euro_precision=0] - Number of decimal places for euro formatting
 * @param {boolean} [props.border] - If true, adds border to each legend item
 * @param {string} [props.className] - Additional class names for container
 * @param {boolean} [props.hideValues] - If true, hides the value from legend
 * @param {boolean} [props.horizontal] - If true, renders legend in horizontal layout
 * @returns {JSX.Element}
 */
const ChartLegend = ({
  data,
  percentage,
  euro_precision = 0,
  border,
  className,
  hideValues,
  horizontal,
}) => {
  const [chartLegends, setChartLegends] = useState([]);
  const { hoveredIndex } = useReactiveVar(chart);

  const chartClass = classNames({
    "chart-legend": true,
    [className]: className,
    "with-percentage": percentage,
    "is-hovered": hoveredIndex !== null,
    horizontal,
  });

  /**
   * Generates chart legends from data provided.
   * Supports both array (with color and value) and object forms (key-value).
   */
  useEffect(() => {
    if (!data) return;

    let legends = [];

    if (Array.isArray(data)) {
      legends = data.map((item) => ({
        name: item.name,
        value: item.value,
        color: item.color || chart().colors[0], // fallback to first color
      }));
    } else if (typeof data === "object") {
      const keys = Object.keys(data);
      legends = keys.map((key, index) => {
        const dataValue = data[key];

        // Check if data[key] is an object with value and color properties
        if (
          typeof dataValue === "object" &&
          dataValue !== null &&
          "value" in dataValue
        ) {
          return {
            name: key,
            value: dataValue.value,
            color: dataValue.color || chart().colors[index] || "#999",
          };
        } else {
          // Data[key] is a primitive value
          return {
            name: key,
            value: dataValue,
            color: chart().colors[index] || "#999",
          };
        }
      });
    }

    setChartLegends(legends);
  }, [data, percentage, horizontal]);

  return (
    <div className={chartClass}>
      {chartLegends.length > 0 &&
        chartLegends.map((item, index) => {
          const chartItemClass = classNames({
            "chart-legend__item": true,
            "with-border": border,
            "is-hovered": hoveredIndex === index,
          });

          /**
           * Formats the value for display based on mode (percentage or euro).
           * @returns {JSX.Element}
           */
          const valueDiv = () => {
            const value = percentage
              ? item.value + "%"
              : utils.toEuro(item.value, euro_precision);
            return horizontal ? (
              <div className="chart-legend__item__label__value">{value}</div>
            ) : (
              <div className="chart-legend__item__value">{value}</div>
            );
          };

          return (
            <div className={chartItemClass} key={item.name}>
              <div className="chart-legend__item__color">
                <span style={{ backgroundColor: item.color }} />
              </div>
              <div className="chart-legend__item__label">
                <span>{item.name}</span>
                {!hideValues && horizontal && valueDiv()}
              </div>
              {!hideValues && !horizontal && valueDiv()}
            </div>
          );
        })}
    </div>
  );
};

export default ChartLegend;
